import { z } from 'zod';

// Stax configuration
interface StaxConfig {
  apiKey: string;
  publicKey: string;
  merchantId: string;
  baseUrl: string;
}

let staxClient: any = null;

export function initializeStax(config: StaxConfig) {
  // In a real app, you'd initialize a client with these details
  staxClient = {
    config,
    // Mocked methods
    transactions: {
      charge: async (params: any) => {
        console.log('Charging with Stax:', params);
        return {
          id: `stax_${Date.now()}`,
          success: true,
          message: 'Transaction successful',
        };
      },
    },
  };
  console.log('Stax initialized with config:', config);
}

export function getStaxClient() {
  if (!staxClient) {
    console.warn('Stax client has not been initialized.');
  }
  return staxClient;
}

// Discount Code Validation
interface DiscountCode {
  id: string;
  code: string;
  code_name: string;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  plan_codes: string[] | null;
  applies_to_billing: string[] | null;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  description: string;
  terms_conditions: string;
}

interface ValidationResult {
  valid: boolean;
  reason?: string;
}

export function validateDiscountCode(
  discount: DiscountCode,
  planCode: string,
  billingFrequency: 'monthly' | 'annual'
): ValidationResult {
  if (!discount.is_active) {
    return { valid: false, reason: 'This code is no longer active.' };
  }

  const now = new Date();
  if (discount.valid_from && new Date(discount.valid_from) > now) {
    return { valid: false, reason: 'This code is not yet valid.' };
  }
  if (discount.valid_until && new Date(discount.valid_until) < now) {
    return { valid: false, reason: 'This code has expired.' };
  }

  if (discount.plan_codes && discount.plan_codes.length > 0 && !discount.plan_codes.includes(planCode)) {
    return { valid: false, reason: `This code is not valid for the ${planCode} plan.` };
  }

  if (discount.applies_to_billing && discount.applies_to_billing.length > 0 && !discount.applies_to_billing.includes(billingFrequency)) {
    return { valid: false, reason: `This code is not valid for ${billingFrequency} billing.` };
  }

  return { valid: true };
}

// Price Calculation
export function calculateDiscount(
  basePrice: number,
  discountType: 'percentage' | 'fixed_amount',
  discountValue: number
): number {
  if (discountType === 'percentage') {
    return Math.round(basePrice * (discountValue / 100));
  }
  if (discountType === 'fixed_amount') {
    return discountValue;
  }
  return 0;
}

// Formatting
export function formatAmount(price: number): string {
  // Prices in this app are stored as integer cents (e.g. 1699 => $16.99).
  const dollars = Number(price) / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(dollars);
}

// Transaction schema and processing
const staxTransactionSchema = z.object({
  id: z.string(),
  amount: z.number(),
  currency: z.string(),
  status: z.string(),
});

export type StaxTransaction = z.infer<typeof staxTransactionSchema>;

export const processPayment = async (amount: number, token: string): Promise<StaxTransaction> => {
  console.log(`Processing payment of ${amount} with token ${token}`);
  // Mock implementation
  return {
    id: `txn_${Math.random().toString(36).substr(2, 9)}`,
    amount,
    currency: 'USD',
    status: 'succeeded',
  };
};
